'use client';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';

export default function NavigationProgress() {
  const pathname = usePathname();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const prevPathname = useRef(pathname);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startProgress = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);

    setLoading(true);
    setVisible(true);
    setProgress(15);

    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 88) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 88;
        }
        // Faster at start, slower near end
        const increment = prev < 40 ? Math.random() * 15 : prev < 70 ? Math.random() * 8 : Math.random() * 3;
        return Math.min(prev + increment, 88);
      });
    }, 150);
  }, []);

  const completeProgress = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setProgress(100);
    completeTimerRef.current = setTimeout(() => {
      setLoading(false);
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 300);
    }, 350);
  }, []);

  // Complete on pathname change
  useEffect(() => {
    if (prevPathname.current !== pathname) {
      completeProgress();
      prevPathname.current = pathname;
    }
  }, [pathname, completeProgress]);

  // Intercept link clicks
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a');
      if (!target) return;
      const href = target.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('http') || href.startsWith('mailto') || href.startsWith('tel')) return;
      if (href === pathname) return;
      startProgress();
    };

    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      if (timerRef.current) clearInterval(timerRef.current);
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [pathname, startProgress]);

  if (!visible) return null;

  return (
    <>
      {/* Top progress bar */}
      <div
        className="fixed top-0 left-0 z-[9999] pointer-events-none"
        style={{
          width: `${progress}%`,
          height: '3px',
          background: 'linear-gradient(90deg, #b8860b 0%, #f0c040 50%, #ffe066 100%)',
          boxShadow: '0 0 12px 3px rgba(240,192,64,0.8), 0 0 4px 1px rgba(240,192,64,1)',
          opacity: visible ? 1 : 0,
          transition: progress === 100
            ? 'width 0.15s ease-out, opacity 0.3s ease 0.05s' :'width 0.2s ease-out',
          borderRadius: '0 2px 2px 0',
        }}
      />

      {/* Leading glow dot */}
      {loading && progress > 0 && progress < 100 && (
        <div
          className="fixed top-0 z-[9999] pointer-events-none"
          style={{
            left: `calc(${progress}% - 5px)`,
            width: '10px',
            height: '3px',
            background: '#ffe066',
            borderRadius: '50%',
            boxShadow: '0 0 8px 3px rgba(255,224,102,0.9)',
            transition: 'left 0.2s ease-out',
          }}
        />
      )}

      {/* Overlay — subtle, non-blocking */}
      {loading && (
        <div
          className="fixed inset-0 z-[9990] pointer-events-none"
          style={{
            backgroundColor: 'rgba(7, 14, 31, 0.35)',
            backdropFilter: 'blur(0.5px)',
            transition: 'opacity 0.15s ease',
          }}
        />
      )}

      {/* Centered spinner */}
      {loading && (
        <div className="fixed inset-0 z-[9991] pointer-events-none flex items-center justify-center">
          <div
            className="flex flex-col items-center gap-3 px-6 py-5 rounded-xl"
            style={{
              backgroundColor: 'rgba(10, 20, 45, 0.92)',
              border: '1px solid rgba(240,192,64,0.35)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(240,192,64,0.12)',
            }}
          >
            <div className="relative w-10 h-10">
              <div
                className="absolute inset-0 rounded-full border-4 animate-spin"
                style={{ borderColor: 'rgba(240,192,64,0.2)', borderTopColor: '#f0c040' }}
              />
              <div
                className="absolute inset-1 rounded-full border-2 animate-spin"
                style={{
                  borderColor: 'rgba(240,192,64,0.12)',
                  borderBottomColor: '#ffe066',
                  animationDirection: 'reverse',
                  animationDuration: '0.55s',
                }}
              />
            </div>
            <span
              className="text-xs font-semibold tracking-widest uppercase"
              style={{ color: '#f0c040', letterSpacing: '0.15em' }}
            >
              Loading…
            </span>
          </div>
        </div>
      )}

      {/* Bottom-right status badge */}
      {loading && (
        <div
          className="fixed bottom-5 right-5 z-[9999] flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold pointer-events-none"
          style={{
            backgroundColor: 'rgba(7,14,31,0.95)',
            border: '1px solid rgba(240,192,64,0.45)',
            color: '#f0c040',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5), 0 0 8px rgba(240,192,64,0.18)',
          }}
        >
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#f0c040' }} />
          System Loading
          <span className="ml-1 font-mono text-[10px] opacity-70" style={{ color: '#d4a017' }}>
            {Math.round(progress)}%
          </span>
        </div>
      )}
    </>
  );
}
